import React, { useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import useIni from '../../hooks/useIni';
import { useNomenclator } from '../../contexts/nomenclatorContext';
import { nomEleccionAmbitos, abbrEleccionACodigoEleccion } from 'constantes';
import styled from 'styled-components/macro';
import { useViewContext } from '../../contexts/viewContext';
import useMeasure from 'use-measure';
import usePosition from 'hooks/usePosition';
import LinkWithTop from '../Links/LinkWithTop';

const linkVariants = {
  start: {
    margin: '0px 0px 0px 20px',
    opacity: 0,
  },
  end: {
    margin: '0px 0px 0px 0px',
    opacity: 1,
  },
};

const pillVariants = {
  idle: {
    margin: '10px 0px 10px 12px',
    paddingLeft: '10px',
  },
  selected: {
    margin: '10px 0px 10px 0px',
    paddingLeft: '20px',
  },
};

const time = 0.6;

const colorBarVariants = {
  open: {
    height: '200vh',
    transition: {
      delay: time,
      duration: time,
    },
  },
  closed: {
    height: '0vh',
    transition: {
      duration: time,
    },
  },
};

const Link = ({ text, color, colorBarContainerRef, _key, variant, to }) => {
  const ref = useRef(null);
  const measure = useMeasure(ref);
  const position = usePosition(ref);
  const [hovered, setHovered] = useState(false);
  const esHistorico = useViewContext().esHistorico;

  return (
    <Li
      layoutId={_key}
      variants={linkVariants}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <StyledLink
        to={to}
        tabIndex={
          variant === 'medium' || hovered || esHistorico ? '-1' : undefined
        }
        disabled={esHistorico}
      >
        <PillContainer>
          <ColorPill
            className="color-pill"
            ref={ref}
            animate={variant === 'medium' || hovered ? 'selected' : 'idle'}
            variants={pillVariants}
            color={color}
            variant={variant}
            disabled={esHistorico}
          >
            <AnimatePresence>
              {colorBarContainerRef?.current &&
                variant === 'medium' &&
                measure &&
                position && (
                  <div>
                    {ReactDOM.createPortal(
                      <ColorBarContainer
                        style={
                          // prettier-ignore
                          {
                            top: position ? position.top + (measure.height / 2) : 0,
                          }
                        }
                      >
                        <ColorBar
                          variants={colorBarVariants}
                          initial="closed"
                          animate="open"
                          exit="closed"
                          color={color}
                        />
                      </ColorBarContainer>,
                      colorBarContainerRef.current
                    )}
                  </div>
                )}
            </AnimatePresence>
            {text}
          </ColorPill>
        </PillContainer>
      </StyledLink>
    </Li>
  );
};

const Li = styled(motion.li)`
  width: 100%;
`;

const StyledLink = styled(LinkWithTop)`
  width: 100%;
  transition: transform 0.4s;
  display: block;
  text-decoration-color: transparent !important;
  & * {
    text-decoration-color: transparent !important;
  }
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'unset')};
  &:focus {
    & .color-pill {
      text-decoration: none !important;
      border-color: black;
    }
  }
  &:hover {
    text-decoration: none;
  }
`;

const PillContainer = styled(motion.div)``;

const ColorPill = styled(motion.div)`
  border-top-left-radius: 1.5rem;
  border-bottom-left-radius: 1.5rem;
  background-color: ${({ color, variant, disabled }) =>
    variant === 'medium' ? color : disabled ? 'dimgrey' : '#215CCC'};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 25px;
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 0.25rem 0;
  text-align: center;
  border: 2px solid transparent;
`;

const ColorBar = styled(motion.div)`
  width: inherit;
  background-color: ${({ color }) => color};
  pointer-events: none;
`;

const ColorBarContainer = styled(motion.div)`
  width: inherit;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 1px;
`;

const LinksList = ({ variant = 'small', colorBarContainerRef, open }) => {
  const ini = useIni();
  const nomenclator = useNomenclator();
  const { page, elections, view } = useViewContext();

  const links = useMemo(
    () =>
      view === 'index'
        ? []
        : computeElections(ini?.botones, elections, nomenclator.ordAmbitos),
    [elections, ini.botones, nomenclator.ordAmbitos, view]
  );

  return (
    <AnimatePresence>
      {links.length && (
        <Ul
          transition={{
            staggerChildren: 0.15,
            delayChildren: 0.2,
          }}
          animate="end"
          initial="start"
          exit="start"
        >
          {links.map((elec, i) => {
            return (
              <Link
                i={i}
                key={elec.eleccion}
                to={elec.path}
                colorBarContainerRef={colorBarContainerRef}
                page={elec.page}
                color={nomenclator.nomAmbitos[elec.eleccion].cs}
                variant={page === elec.page ? 'medium' : variant}
                text={elec.name}
              />
            );
          })}
        </Ul>
      )}
    </AnimatePresence>
  );
};

const Ul = styled(motion.ul)`
  max-width: 30rem;
`;

export default LinksList;

const computeElections = (iniButtons, ambitos, ordenAmbitos) => {
  const activeButtons = iniButtons
    .filter(b => b.hab === 'activado')
    .map(b => b.id);

  const activeAmbitos = ambitos?.filter(({ eleccion }) =>
    activeButtons.includes(abbrEleccionACodigoEleccion[eleccion])
  );

  const elections = activeAmbitos.map(({ eleccion, url }) => ({
    eleccion: abbrEleccionACodigoEleccion[eleccion],
    path: url,
    name: nomEleccionAmbitos[eleccion].toUpperCase(),
    page: nomEleccionAmbitos[eleccion],
  }));
  elections.sort(
    (a, b) =>
      ordenAmbitos.indexOf(a.eleccion) - ordenAmbitos.indexOf(b.eleccion)
  );
  return elections;
};
